import * as THREE from 'three';
import { fract, pulse } from './critter-math.js';
import { bakeCritter, parts } from './critter-parts.js';
import { standHeight, type QuadSpecies } from './quad-species.js';

const TAU = Math.PI * 2;

/** What the grazer is doing, described by amounts so every state blends into the next. */
export interface QuadAction {
  /** 0–1 how much it is walking, where in the stride it is (whole cycles), and 0 walk … 1 gallop. */
  walk: number; stride: number; gait: number;
  /** 0–1 head down to the grass, and 0–1 head high with ears pricked. */
  graze: number; alert: number;
  /** Seconds on the animal's own idle clock: breathing, ear flicks, looking about. */
  idle: number;
}
export const restingQuad = (): QuadAction => ({ walk: 0, stride: 0, gait: 0, graze: 0, alert: 0, idle: 0 });

/**
 * A low-poly four-legged grazer facing +Z, dressed as the given species (deer or sheep). Each leg has a hip and a knee and a hoof
 * that stays level; the neck and head swing between grazing and watching; ears flick on their own; the torso rides on the legs.
 */
export class QuadModel {
  readonly root = new THREE.Group();
  private torso = new THREE.Group();
  private neck = new THREE.Group();
  private head = new THREE.Group();
  private tail = new THREE.Group();
  private muzzle: THREE.Mesh;
  private muzzleBase = 0;
  private ears: THREE.Group[] = [];
  private legs: { hip: THREE.Group; knee: THREE.Group; hoof: THREE.Mesh; front: boolean }[] = [];

  constructor(readonly species: QuadSpecies, private seed = 0, tone = 0) {
    const s = species, [rx, ry, rz] = s.torso, { torso, neck, head, tail } = this, coat = s.coat, shade = (value: number) => new THREE.Color(value).multiplyScalar(1 - tone * .08).getHex();
    this.root.add(torso);
    parts.blob(torso, shade(coat), [rx, ry, rz], [0, 0, 0]);
    parts.blob(torso, s.belly, [rx * .85, ry * .5, rz * .85], [0, -ry * .5, 0]);
    parts.blob(torso, shade(coat), [rx * .95, ry * .95, rz * .5], [0, .02, -rz * .62]);
    parts.blob(torso, shade(coat), [rx * .9, ry * .95, rz * .45], [0, .03, rz * .62]);
    if (s.wool) {
      // A fleece of overlapping tufts, so the outline is lumpy rather than a smooth egg.
      for (let i = 0; i < 16; i++) {
        const a = i * 2.399, row = (i % 4) / 3 - .5, x = Math.sin(a) * rx * .85, y = Math.cos(a) * ry * .8 + ry * .1, z = row * rz * 1.6;
        if (y > -ry * .55) parts.blob(torso, shade(coat), [rx * .45, ry * .42, rx * .5], [x, y, z]);
      }
    }
    neck.position.set(0, ry * .5, rz * .78); torso.add(neck);
    parts.blob(neck, shade(coat), [rx * .42, ry * .42, s.neck * .62], [0, 0, s.neck * .42]);
    head.position.set(0, 0, s.neck * .92); neck.add(head);
    const [hx, hy, hz] = s.head;
    parts.blob(head, s.face, [hx, hy, hz], [0, 0, hz * .35]);
    this.muzzle = parts.blob(head, s.wool ? s.face : s.belly, [hx * .72, hy * .62, hz * .55], [0, -hy * .22, hz * .95]);
    this.muzzleBase = this.muzzle.position.y;
    parts.blob(head, 0x1c1614, [hx * .32, hy * .28, hx * .3], [0, -hy * .1, hz * 1.5]);
    for (const side of [-1, 1]) {
      parts.blob(head, 0x16100e, [.012, .014, .012], [side * hx * .85, hy * .35, hz * .45]);
      const ear = new THREE.Group(); ear.position.set(side * hx * .7, hy * .75, -hz * .05); head.add(ear);
      parts.blob(ear, s.face, [.028, s.earLength * .5, .012], [0, s.earLength * .5, 0]);
      parts.blob(ear, s.inner, [.016, s.earLength * .38, .006], [0, s.earLength * .47, .006]);
      this.ears.push(ear);
      if (s.antlers) {
        // A main beam sweeping back and up with two tines.
        const beam = new THREE.Group(); beam.position.set(side * hx * .5, hy * .9, -hz * .1); beam.rotation.set(-.35, 0, -side * .35); head.add(beam);
        parts.stick(beam, 0xb9a47f, .01, .32).position.y = .16;
        for (const [y, forward] of [[.12, 1], [.22, -1]] as const) { const tine = parts.stick(beam, 0xb9a47f, .008, .13); tine.position.set(0, y, forward * .035); tine.rotation.x = forward * .6; }
      }
    }
    tail.position.set(0, ry * .35, -rz * .95); torso.add(tail);
    if (s.flag) parts.blob(tail, 0xf1ead9, [.045, .075, .04], [0, .02, -.02]); else parts.blob(tail, s.coat, [.05, .06, .05], [0, -.03, -.02]);
    // Legs: hip and knee, a thin shin and a dark hoof.
    for (const [side, front] of [[-1, true], [1, true], [-1, false], [1, false]] as const) {
      const hip = new THREE.Group(); hip.position.set(side * rx * .58, -ry * .4, front ? rz * .62 : -rz * .62); torso.add(hip);
      parts.blob(hip, shade(coat), [s.legRadius * 1.9, s.legUpper * .55, s.legRadius * 2.1], [0, -s.legUpper * .4, 0]);
      const knee = new THREE.Group(); knee.position.y = -s.legUpper; hip.add(knee);
      parts.stick(knee, s.legs, s.legRadius, s.legLower).position.y = -s.legLower / 2;
      const hoof = parts.blob(knee, s.hoof, [s.legRadius * 1.5, s.legRadius * .9, s.legRadius * 2], [0, -s.legLower, s.legRadius * .4]);
      this.legs.push({ hip, knee, hoof, front });
    }
    bakeCritter(this.root);
  }

  apply(a: QuadAction) {
    const s = this.species, L = s.legUpper + s.legLower, H = standHeight(s), t = a.idle + this.seed;
    const gallop = a.gait > .5, offsets = gallop ? [0, .08, .45, .53] : [.25, .75, 0, .5]; // front left, front right, hind left, hind right
    const amplitude = (.34 + .5 * a.gait) * a.walk, flex = .55 + .55 * a.gait;
    let splay = Infinity;
    this.legs.forEach((leg, index) => {
      const cycle = a.stride + offsets[index], swing = Math.sin(cycle * TAU) * amplitude, lift = Math.max(0, -Math.cos(cycle * TAU)) * flex * a.walk;
      // The body sinks only as far as its most upright leg allows, so no hoof is ever driven into the ground.
      splay = Math.min(splay, L * (1 - Math.cos(swing)));
      leg.hip.rotation.x = swing + (leg.front ? a.graze * .1 : 0);
      leg.knee.rotation.x = lift + (leg.front ? 0 : lift * .35);
      leg.hoof.rotation.x = -swing * .8 - lift * .6;
    });
    const bound = Math.max(0, Math.sin((a.stride + .1) * TAU)) * .09 * a.gait * a.walk;
    this.torso.position.set(0, H - splay + bound + Math.sin(t * 1.8) * .004, 0);
    this.torso.rotation.set(a.graze * .1 + Math.sin(a.stride * TAU) * .07 * a.gait * a.walk, 0, 0);
    this.torso.scale.y = 1 + Math.sin(t * 1.8) * .012;

    // Head and neck: up and watching, or down at the grass chewing; they look about slowly when standing.
    const look = (Math.sin(t * .47) * .6 + Math.sin(t * 1.3 + 1) * .25) * (1 - a.walk * .6) * (1 - a.graze * .5);
    const chew = Math.sin(t * 8.5) * a.graze, nod = Math.sin(a.stride * TAU * 2) * .05 * a.walk;
    this.neck.rotation.set(THREE.MathUtils.lerp(-.7 - a.alert * .3, 1.2, a.graze) + nod, look * .5, 0);
    this.head.rotation.set(THREE.MathUtils.lerp(.78 + a.alert * .1, -.25, a.graze), look * .4, 0);
    this.muzzle.position.y = this.muzzleBase - Math.abs(chew) * .008;
    this.ears.forEach((ear, index) => {
      const side = index ? 1 : -1, flickCycle = fract(t / (7.3 + index * 2.1) + index * .4), flick = pulse(flickCycle, .6, .05);
      ear.rotation.set(-a.alert * .35 + flick * .5 - a.gait * .5, 0, side * (.75 - a.alert * .3 + a.graze * .1) + side * flick * .2);
    });
    this.tail.rotation.set(-(s.flag ? a.gait * 1.1 + a.alert * .5 : 0) + Math.sin(t * 2.1) * .12 * (1 + a.walk), Math.sin(t * 5) * .1 * (1 - a.graze), 0);
  }
}
